import { type PropsWithChildren, useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { useGetCurrentUser } from '@wecasa/auth/src/authentication-context';

import { useUniverse } from '@wecasa/common/src/providers/universes-provider';
import { toRem } from '@wecasa/common/src/utils/styles';
import { isChildcare, isCleaning, isWellness } from '@wecasa/common/src/utils/universes';

import type {
  CommonDispatchBasketProps,
  CommonOwnBasketProps,
  CommonStateBasketProps,
} from '@wecasa/customer/src/components/basket/types';
import { isSpotCleaningSelector, setBasketAttribute } from '@wecasa/customer/src/ducks/basket';
import {
  getAllValidProChoices,
  getFirstPrestation,
  getPrestationDuration,
  getUniverseRef,
} from '@wecasa/customer/src/ducks/basket-selectors';

import { translate } from '@wecasa/i18n';

import useCanSubscribeToMembership from '@wecasa/membership/src/hooks/use-can-subscribe-to-membership';
import useHasOrWantsMembership from '@wecasa/membership/src/hooks/use-has-or-wants-membership';
import useShouldShowMembershipCard from '@wecasa/membership/src/hooks/use-should-show-membership-card';

import Title from '@wecasa/web-ui/src/components/base-text/variants/title';
import Card from '@wecasa/web-ui/src/components/card/card';
import { DesktopAndTablet, Mobile } from '@wecasa/web-ui/src/styles/media-queries';

import ChildcareBasket from '../../components/basket/childcare/childcare-basket';
import CleaningBasket from '../../components/basket/cleaning/cleaning-basket';
import WellnessBasket from '../../components/basket/wellness/wellness-basket';
import MembershipPaywall from '../../membership/membership-paywall/membership-paywall';
import useMembershipPaywall from '../../membership/membership-paywall/use-membership-paywall';
import type { RootState } from '../../store';
import { HEADER_HEIGHT } from '../main-container/header';

const Container = styled.div`
  width: 100%;
`;

const DesktopCard = styled(Card)`
  padding: 40px 0;
  position: sticky;
  top: ${toRem(HEADER_HEIGHT + 5)};
  left: 0;
  right: 0;
`;

const FullScreenWrapper = styled.div`
  padding: ${toRem(20)} 0 ${toRem(40)};
`;

const DesktopTitle = styled(Title.MD)`
  margin: 0 0 20px;
`;

const t = translate('containers.basket');

type StateProps = CommonStateBasketProps;

export type OwnProps = CommonOwnBasketProps & {
  fullScreen?: boolean;
  pathname: string;
};

export type BasketProps = StateProps & CommonDispatchBasketProps & OwnProps;

export const Basket = ({
  activities,
  addressDetails,
  appointment,
  appointmentDate,
  basketAddress,
  basketContent,
  basketContentDuration,
  children,
  childrenCount,
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  fullScreen = false,
  hasFreeParking,
  hasPets,
  isCleaningSpot,
  mode,
  pathname,
  prestation,
  proChoices = [],
  proGender,
  promotion,
  setAddressDetails,
  setIsMembershipRequested,
  shouldShowPromoCode = false,
  shouldUpdateAllOccurrences,
  toggleHasFreeParking,
  universeReference,
  weeklyRecurrence,
  youngestChildAge,
}: PropsWithChildren<BasketProps>) => {
  const universe = useUniverse(universeReference);
  const user = useGetCurrentUser();
  const hasMembership = useHasOrWantsMembership();
  const canSubscribeToMembership = useCanSubscribeToMembership({
    user,
    universeReference,
    isCleaningSpot,
  });
  const shouldShowMembershipCard = useShouldShowMembershipCard({
    mode,
    user,
    isCleaningSpot,
    hasOrWantsMembership: hasMembership,
    universeReference,
  });
  const { isMembershipPaywallVisible, openMembershipPaywall, closeMembershipPaywall } = useMembershipPaywall();

  const renderedBasket = useMemo(() => {
    if (!universe) {
      return null;
    }

    if (isChildcare(universe)) {
      return (
        <ChildcareBasket
          prestation={prestation}
          duration={duration}
          activities={activities}
          address={basketAddress}
          childrenCount={childrenCount}
          youngestChildAge={youngestChildAge}
          appointment={appointment}
          user={user}
          promotion={promotion}
          universe={universe}
          proChoices={proChoices}
          addressDetails={addressDetails}
          setAddressDetails={setAddressDetails}
          hasFreeParking={hasFreeParking}
          toggleHasFreeParking={toggleHasFreeParking}
          shouldShowPromoCode={shouldShowPromoCode}
        >
          {children}
        </ChildcareBasket>
      );
    }

    if (isCleaning(universe)) {
      return (
        <CleaningBasket
          address={basketAddress}
          addressDetails={addressDetails}
          appointment={appointment}
          appointmentDate={appointmentDate}
          canSubscribeToMembership={canSubscribeToMembership}
          cleaningPrestation={cleaningPrestation}
          cleaningPrestationOptions={cleaningPrestationOptions}
          duration={duration}
          hasFreeParking={hasFreeParking}
          hasMembership={hasMembership}
          hasPets={hasPets}
          mode={mode}
          openMembershipPaywall={openMembershipPaywall}
          pathname={pathname}
          proChoices={proChoices}
          promotion={promotion}
          setAddressDetails={setAddressDetails}
          setIsMembershipRequested={setIsMembershipRequested}
          shouldShowMembershipCard={shouldShowMembershipCard}
          shouldShowPromoCode={shouldShowPromoCode}
          shouldUpdateAllOccurrences={shouldUpdateAllOccurrences}
          toggleHasFreeParking={toggleHasFreeParking}
          universeReference={universe.reference}
          user={user}
          weeklyRecurrence={weeklyRecurrence}
          weeklyRecurrences={universe.weekly_recurrences}
        >
          {children}
        </CleaningBasket>
      );
    }

    if (isWellness(universe)) {
      return (
        <WellnessBasket
          basketContent={basketContent}
          universe={universe}
          promotion={promotion}
          appointment={appointment}
          address={basketAddress}
          proGender={proGender}
          proChoices={proChoices}
          addressDetails={addressDetails}
          setAddressDetails={setAddressDetails}
          basketContentDuration={basketContentDuration}
          hasFreeParking={hasFreeParking}
          toggleHasFreeParking={toggleHasFreeParking}
          canSubscribeToMembership={canSubscribeToMembership}
          shouldShowMembershipCard={shouldShowMembershipCard}
          hasMembership={hasMembership}
          setIsMembershipRequested={setIsMembershipRequested}
          openMembershipPaywall={openMembershipPaywall}
          pathname={pathname}
          user={user}
          shouldShowPromoCode={shouldShowPromoCode}
        >
          {children}
        </WellnessBasket>
      );
    }

    return null;
  }, [
    universe,
    prestation,
    duration,
    activities,
    basketAddress,
    childrenCount,
    youngestChildAge,
    appointment,
    user,
    promotion,
    proChoices,
    addressDetails,
    setAddressDetails,
    hasFreeParking,
    toggleHasFreeParking,
    shouldShowPromoCode,
    children,
    appointmentDate,
    cleaningPrestation,
    cleaningPrestationOptions,
    hasPets,
    mode,
    weeklyRecurrence,
    canSubscribeToMembership,
    shouldShowMembershipCard,
    hasMembership,
    setIsMembershipRequested,
    openMembershipPaywall,
    shouldUpdateAllOccurrences,
    pathname,
    basketContent,
    proGender,
    basketContentDuration,
  ]);

  return (
    <Container>
      <MembershipPaywall isOpen={isMembershipPaywallVisible} onClose={closeMembershipPaywall} />
      <DesktopAndTablet>
        {fullScreen ? (
          <FullScreenWrapper>{renderedBasket}</FullScreenWrapper>
        ) : (
          <DesktopCard>
            <DesktopTitle centered>{t('.my_basket')}</DesktopTitle>
            {renderedBasket}
          </DesktopCard>
        )}
      </DesktopAndTablet>
      <Mobile>{renderedBasket}</Mobile>
    </Container>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  activities: state.basket.activities,
  basketAddress: state.basket.address,
  appointment: state.basket.appointment,
  appointmentDate: state.basket.appointmentDate,
  basketContent: state.basket.basketContent,
  cleaningPrestation: state.basket.cleaningPrestation,
  cleaningPrestationOptions: state.basket.cleaningPrestationOptions,
  childrenCount: state.basket.childrenParams.childrenCount,
  mode: state.basket.mode,
  duration: state.basket.duration,
  hasPets: state.basket.hasPets,
  isCleaningSpot: isSpotCleaningSelector(state),
  promotion: state.basket.promotion,
  prestation: getFirstPrestation(state),
  basketContentDuration: getPrestationDuration(state),
  proChoices: getAllValidProChoices(state),
  proGender: state.basket.proGender,
  universeReference: getUniverseRef(state),
  weeklyRecurrence: state.basket.weekly_recurrence,
  youngestChildAge: state.basket.childrenParams.youngestChildAge,
  shouldUpdateAllOccurrences: state.basket.updateAllOccurrences,
});

const mapDispatchToProps: CommonDispatchBasketProps = {
  setIsMembershipRequested: setBasketAttribute('isMembershipRequested'),
};

export default connect(mapStateToProps, mapDispatchToProps)(Basket);
